import { useSearch } from '@tanstack/react-router';
import { Box, CircularProgress, Container, Paper, Typography, Alert } from '@mui/material';
import { useGetDashboardLayoutQuery } from './api/dashboardApi';
import { ActivityBlockSection } from './components/ActivityBlockSection';
import { QuickLinkSection } from './components/QuickLinkSection';
import { DashboardGridSection } from './components/DashboardGridSection';
import { ColleagueRatingWidget } from '../colleagueRating/ColleagueRatingWidget';
import { DEFAULT_DASHBOARD_NODE_KEY } from './constants';
import type { DashboardBlock } from './api/dashboardApi';

const GRID_BLOCK_TYPES = new Set([
  'zDashboardBlockGridSection',
  'zDashboardBlockGrid2Section',
  'zDashboardBlockSpecification',
]);

const FULL_WIDTH_MOBILE_BLOCK_TYPES = new Set(['zActivityBlock']);

/** Convert Bootstrap col-N class to a CSS width percentage. */
function colToWidth(widthCss: string): string {
  const match = /col-(?:(?:xs|sm|md|lg|xl)-)?(\d+)$/.exec(widthCss);
  if (match) {
    const n = parseInt(match[1], 10);
    return `${(n / 12) * 100}%`;
  }
  return '100%';
}

function BlockRenderer({ block }: { block: DashboardBlock }) {
  if (block.type === 'zActivityBlock') {
    return (
      <ActivityBlockSection
        blockKey={block.key}
        title={block.title}
        relatedLinkUrl={block.relatedLinkUrl}
        relatedLinkCaption={block.relatedLinkCaption}
      />
    );
  }

  if (block.type === 'zColleagueRatingBlock') {
    return <ColleagueRatingWidget />;
  }

  if (GRID_BLOCK_TYPES.has(block.type)) {
    return <DashboardGridSection nodeKey={block.key} />;
  }

  if (block.type === 'zSiteQuickLinkBlock' && block.links?.length) {
    return (
      <QuickLinkSection
        links={block.links}
        title={block.title}
        icon={block.icon}
        backgroundColor={block.backgroundColor}
        backgroundColorDarkTheme={block.backgroundColorDarkTheme}
      />
    );
  }

  return null;
}

export const DashboardPage = () => {
  const search = useSearch({ strict: false }) as { nodeKey?: string };
  const nodeKey = search.nodeKey ?? DEFAULT_DASHBOARD_NODE_KEY;

  const { data: layout, isLoading, isError } = useGetDashboardLayoutQuery(nodeKey);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !layout) {
    return (
      <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">Failed to load dashboard layout</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
      <Box sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {layout.rows.map((row, rowIdx) => (
          <Box
            key={rowIdx}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: { xs: 'space-between', sm: 'flex-start' },
              rowGap: { xs: '10px' },
              gap: { sm: '10px' },
              minHeight: row.minHeight > 0 ? row.minHeight : undefined,
            }}
          >
            {row.blocks.map((block) => {
              const content = <BlockRenderer block={block} />;
              if (!content) return null;

              const needsCard =
                block.type === 'zActivityBlock' ||
                block.type === 'zColleagueRatingBlock' ||
                GRID_BLOCK_TYPES.has(block.type);
              const isQuickLink = block.type === 'zSiteQuickLinkBlock';
              const isFullWidthMobileBlock = FULL_WIDTH_MOBILE_BLOCK_TYPES.has(block.type);

              return (
                <Box
                  key={block.key}
                  sx={{
                    width: isFullWidthMobileBlock
                      ? { xs: '100%', sm: `calc(${colToWidth(block.widthCss)} - 8px)` }
                      : isQuickLink
                        ? { xs: 'calc(50% - 8px)', sm: `calc(${colToWidth(block.widthCss)} - 8px)` }
                        : `calc(${colToWidth(block.widthCss)} - 8px)`,
                    minWidth: isFullWidthMobileBlock
                      ? { xs: 0, sm: 200 }
                      : isQuickLink
                        ? { xs: 0, sm: 200 }
                        : 200,
                    flexShrink: 0,
                    ...(isQuickLink && {
                      '@media (max-width: 599.95px)': {
                        '&:nth-last-of-type(1):nth-child(odd)': {
                          width: '100%',
                          maxWidth: '100%',
                        },
                      },
                    }),
                  }}
                >
                  {needsCard ? (
                    <Paper sx={{ p: 2, height: '100%' }}>
                      {block.title && (
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                          {block.icon && <i className={block.icon} style={{ marginRight: 8 }} />}
                          {block.title}
                        </Typography>
                      )}
                      {content}
                    </Paper>
                  ) : (
                    content
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Container>
  );
};
